import { useState, useRef } from 'react';
import { API } from '../config/api';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';
import { Upload, Sparkles, ZoomIn, Loader2, Download, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ImageEnhance() {
  const [originalImage, setOriginalImage] = useState(null);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [mode, setMode] = useState(null);
  const [info, setInfo] = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Max file size is 20MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setOriginalImage(reader.result);
      setEnhancedImage(null);
      setInfo(null);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const enhance = async (target) => {
    if (!originalImage) return;
    setEnhancing(true);
    setMode(target);
    try {
      const base64 = originalImage.split(',')[1];
      const { data } = await api.post(`${API}/artwork/enhance`, {
        image_base64: base64,
        target
      }, { timeout: 60000 });
      
      if (data.image_base64) {
        setEnhancedImage('data:image/png;base64,' + data.image_base64);
        setInfo(data);
        toast.success(`Enhanced to ${data.enhanced_size}!`);
      } else {
        toast.error(data.detail || 'Enhancement failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enhancement failed');
    }
    setEnhancing(false);
  };

  const downloadImage = () => {
    if (!enhancedImage) return;
    const a = document.createElement('a');
    a.href = enhancedImage;
    a.download = `enhanced_${info?.enhanced_size || '4k'}.png`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-white" data-testid="image-enhance-page">
      {/* Header */}
      <div className="border-b border-[#262626] px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-[#666] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-black uppercase tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                HD Image Enhance
              </h1>
              <p className="text-xs text-[#666]">Deblur, sharpen & upscale images to 4K</p>
            </div>
          </div>
          {enhancedImage && (
            <button onClick={downloadImage}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white transition-all hover:brightness-110"
              style={{ background: 'var(--primary)', borderRadius: 'var(--radius)' }}
              data-testid="download-enhanced-btn">
              <Download className="w-3.5 h-3.5" /> DOWNLOAD
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {!originalImage ? (
          /* Upload area */
          <div className="flex flex-col items-center justify-center py-20">
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            <button onClick={() => fileRef.current?.click()}
              className="group flex flex-col items-center gap-4 p-12 border-2 border-dashed border-[#333] hover:border-[var(--primary)] transition-all cursor-pointer"
              style={{ borderRadius: 'var(--radius)' }}
              data-testid="upload-enhance-btn">
              <div className="w-16 h-16 flex items-center justify-center bg-white/5 group-hover:bg-[var(--primary)]/10 transition-colors"
                style={{ borderRadius: 'var(--radius)' }}>
                <Upload className="w-8 h-8 text-[#555] group-hover:text-[var(--primary)] transition-colors" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold">Upload a blurry or low-res image</p>
                <p className="text-xs text-[#666] mt-1">PNG, JPG, WebP up to 20MB</p>
              </div>
            </button>
          </div>
        ) : (
          /* Image comparison + controls */
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex items-center gap-3 justify-center">
              <button onClick={() => enhance('1080p')} disabled={enhancing}
                className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold border transition-all disabled:opacity-50"
                style={{
                  borderRadius: 'var(--radius)',
                  background: mode === '1080p' && enhancedImage ? 'var(--primary)' : 'transparent',
                  borderColor: 'var(--primary)',
                  color: mode === '1080p' && enhancedImage ? '#000' : 'var(--primary)',
                }}
                data-testid="enhance-1080p-standalone">
                {enhancing && mode === '1080p' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ZoomIn className="w-3.5 h-3.5" />}
                UPSCALE TO 1080p
              </button>
              <button onClick={() => enhance('4k')} disabled={enhancing}
                className="flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold text-white transition-all disabled:opacity-50 hover:brightness-110"
                style={{
                  borderRadius: 'var(--radius)',
                  background: 'linear-gradient(135deg, var(--primary), var(--accent))',
                  boxShadow: '0 2px 16px rgba(var(--primary-rgb), 0.35)',
                }}
                data-testid="enhance-4k-standalone">
                {enhancing && mode === '4k' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                UPSCALE TO 4K
              </button>
              <button onClick={() => { setOriginalImage(null); setEnhancedImage(null); setInfo(null); }}
                className="px-3 py-2.5 text-xs font-bold text-[#666] border border-[#333] hover:text-white transition-colors"
                style={{ borderRadius: 'var(--radius)' }}
                data-testid="upload-new-btn">
                NEW IMAGE
              </button>
            </div>

            {/* Before/After comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-[#262626] overflow-hidden" style={{ borderRadius: 'var(--radius)' }}>
                <div className="px-3 py-2 border-b border-[#262626] bg-[#0A0A0A]">
                  <span className="text-[10px] font-bold uppercase text-[#666]">Original</span>
                </div>
                <div className="bg-[#0A0A0A] flex items-center justify-center min-h-[300px] p-2">
                  <img src={originalImage} alt="Original" className="max-w-full max-h-[500px] object-contain" />
                </div>
              </div>
              <div className="border border-[#262626] overflow-hidden" style={{ borderRadius: 'var(--radius)' }}>
                <div className="px-3 py-2 border-b border-[#262626] bg-[#0A0A0A] flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase text-[#0A84FF]">
                    {enhancedImage ? 'Enhanced' : 'Result'}
                  </span>
                  {info && (
                    <span className="text-[10px] font-mono text-[#DAA520]">
                      {info.original_size} → {info.enhanced_size} ({info.scale_factor}x)
                    </span>
                  )}
                </div>
                <div className="bg-[#0A0A0A] flex items-center justify-center min-h-[300px] p-2">
                  {enhancing ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
                      <p className="text-xs text-[#666]">Enhancing to {mode?.toUpperCase()}...</p>
                    </div>
                  ) : enhancedImage ? (
                    <img src={enhancedImage} alt="Enhanced" className="max-w-full max-h-[500px] object-contain" />
                  ) : (
                    <p className="text-xs text-[#444]">Select a quality to enhance</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
